import type { MeasurementSystem } from './distance';

/**
 * Product size units.
 *
 * Sizes are always stored metric: lengths in micrometres, weight in grams,
 * volume in millilitres (ProductSize MeasurementUnit "Generic"). These helpers
 * convert between that storage and the units a market shows and enters.
 * Mirrors app/src/Component/Country/Measurement/MeasurementConverter.php.
 */

export type SizeQuantity = 'length' | 'weight' | 'volume';

export const MICROMETRES_PER_INCH = 25_400;
export const GRAMS_PER_POUND = 453.59237;
export const MILLILITRES_PER_FL_OZ = 29.5735295625;
export const SQUARE_FEET_PER_SQUARE_METRE = 10.763910416709722;
export const FEET_PER_METRE = 3.280839895013123;
export const CUBIC_FEET_PER_CUBIC_METRE = 35.31466672148859;

/** Stored units in one display unit: micrometres per mm or inch, grams per kg or lb, ml per ml or fl oz. */
const STORED_PER_DISPLAY_UNIT: Record<
    MeasurementSystem,
    Record<SizeQuantity, number>
> = {
    metric: { length: 1000, weight: 1000, volume: 1 },
    imperial: {
        length: MICROMETRES_PER_INCH,
        weight: GRAMS_PER_POUND,
        volume: MILLILITRES_PER_FL_OZ,
    },
};

const UNIT_LABELS: Record<MeasurementSystem, Record<SizeQuantity, string>> = {
    metric: { length: 'mm', weight: 'kg', volume: 'ml' },
    imperial: { length: 'in', weight: 'lb', volume: 'fl oz' },
};

/** Display values keep three decimals, enough to round-trip a micrometre-stored inch. */
const DISPLAY_DECIMALS = 3;

export const storedPerDisplayUnit = (
    quantity: SizeQuantity,
    measurementSystem: MeasurementSystem,
): number => STORED_PER_DISPLAY_UNIT[measurementSystem][quantity];

export const sizeUnitLabel = (
    quantity: SizeQuantity,
    measurementSystem: MeasurementSystem,
): string => UNIT_LABELS[measurementSystem][quantity];

/** Rounds to at most `decimals` places: roundTo(12.345, 2) is 12.35. */
export const roundTo = (value: number, decimals: number): number => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

/** Stored metric integer to the number shown in this system (mm/kg/ml or in/lb/fl oz). */
export const storedToDisplay = (
    stored: number,
    quantity: SizeQuantity,
    measurementSystem: MeasurementSystem,
): number =>
    roundTo(
        stored / storedPerDisplayUnit(quantity, measurementSystem),
        DISPLAY_DECIMALS,
    );

/** Number entered in this system back to the stored metric integer. */
export const displayToStored = (
    display: number,
    quantity: SizeQuantity,
    measurementSystem: MeasurementSystem,
): number =>
    Math.round(display * storedPerDisplayUnit(quantity, measurementSystem));

/** Imperial length labels round to the nearest sixteenth of an inch, as US retailers print them. */
const INCH_FRACTION_DENOMINATOR = 16;

const greatestCommonDivisor = (a: number, b: number): number =>
    b === 0 ? a : greatestCommonDivisor(b, a % b);

/**
 * Inches to the nearest sixteenth, reduced, in hyphen form: 35.43 is "35-7/16", 0.125 is "1/8",
 * 7.9999 is "8". A positive length too small to round to a sixteenth shows as "1/16".
 * Mirrors MeasurementConverter::formatInchFraction.
 */
export const formatInchFraction = (inches: number): string => {
    let sixteenths = Math.round(inches * INCH_FRACTION_DENOMINATOR);
    if (sixteenths === 0 && inches > 0) {
        sixteenths = 1;
    }

    const whole = Math.trunc(sixteenths / INCH_FRACTION_DENOMINATOR);
    const numerator = sixteenths % INCH_FRACTION_DENOMINATOR;
    if (numerator === 0) {
        return String(whole);
    }

    const divisor = greatestCommonDivisor(numerator, INCH_FRACTION_DENOMINATOR);
    const fraction = `${numerator / divisor}/${INCH_FRACTION_DENOMINATOR / divisor}`;
    return whole === 0 ? fraction : `${whole}-${fraction}`;
};

/** "35", "35.4375" or ".5": a plain decimal. */
const DECIMAL_PATTERN = /^(\d+(?:\.\d*)?|\.\d+)$/;
/** "35 7/16", "35-7/16" or "7/16": an optional whole number, then a fraction. */
const FRACTION_PATTERN = /^(?:(\d+)[\s-]+)?(\d+)\/(\d+)$/;

/**
 * Reads inches typed as a decimal or a US fraction: "35.4375", "35 7/16", "35-7/16", "7/16"
 * and "12" all read. Null when the text is empty or not a number of inches.
 */
export const parseInches = (text: string): number | null => {
    const trimmed = text.trim();
    if (DECIMAL_PATTERN.test(trimmed)) {
        return Number(trimmed);
    }

    const fraction = FRACTION_PATTERN.exec(trimmed);
    if (!fraction) {
        return null;
    }
    const [, whole = '0', numerator, denominator] = fraction;
    if (Number(denominator) === 0) {
        return null;
    }
    return Number(whole) + Number(numerator) / Number(denominator);
};

export interface PriceUnitDisplay {
    /** "square metre" or "sq ft" */
    label: string;
    /** "m²" or "sq ft" */
    shortLabel: string;
    /** Display units in one stored unit, e.g. 10.76 sq ft per m². Divide a per-stored-unit price by this. */
    perStoredUnit: number;
}

/**
 * Display for each measured price unit, keyed by the Variant.PriceUnit value. Units missing here
 * (Box, Unit, or any added later) show as they are.
 */
const METRIC_PRICE_UNITS: Partial<Record<string, PriceUnitDisplay>> = {
    SquareMetre: { label: 'square metre', shortLabel: 'm²', perStoredUnit: 1 },
    LinearMetre: { label: 'linear metre', shortLabel: 'lm', perStoredUnit: 1 },
    CubicMetre: { label: 'cubic metre', shortLabel: 'm³', perStoredUnit: 1 },
};

const IMPERIAL_PRICE_UNITS: Partial<Record<string, PriceUnitDisplay>> = {
    SquareMetre: {
        label: 'sq ft',
        shortLabel: 'sq ft',
        perStoredUnit: SQUARE_FEET_PER_SQUARE_METRE,
    },
    LinearMetre: {
        label: 'linear ft',
        shortLabel: 'lin ft',
        perStoredUnit: FEET_PER_METRE,
    },
    CubicMetre: {
        label: 'cu ft',
        shortLabel: 'cu ft',
        perStoredUnit: CUBIC_FEET_PER_CUBIC_METRE,
    },
};

/**
 * How a metric price unit reads in this system. Null for units with no
 * measurement (Box, Unit).
 */
export const priceUnitDisplay = (
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): PriceUnitDisplay | null => {
    const units =
        measurementSystem === 'imperial'
            ? IMPERIAL_PRICE_UNITS
            : METRIC_PRICE_UNITS;
    return (priceUnit && units[priceUnit]) || null;
};

/** Price (in cents) per stored metric unit converted to price per display unit. */
export const pricePerDisplayUnit = (
    priceCents: number,
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): number => {
    const display = priceUnitDisplay(priceUnit, measurementSystem);
    return display ? priceCents / display.perStoredUnit : priceCents;
};

/** Area stored in cm² (Variant.CoverageArea) to m² or sq ft. */
export const coverageAreaToDisplay = (
    squareCentimetres: number,
    measurementSystem: MeasurementSystem,
): number => {
    const squareMetres = squareCentimetres / 10_000;
    return roundTo(
        measurementSystem === 'imperial'
            ? squareMetres * SQUARE_FEET_PER_SQUARE_METRE
            : squareMetres,
        2,
    );
};

export const areaUnitLabel = (measurementSystem: MeasurementSystem): string =>
    measurementSystem === 'imperial' ? 'sq ft' : 'm²';
